import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { usersApi } from '../../../api/usersApi';
import { rolesApi, toRoleOptions } from '../../../api/rolesApi';
import { alpha } from '@mui/material/styles';
import { grey, darkVeil } from '../../../theme/tones';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 600,
    borderRadius: '20px',
    border: `1px solid ${grey(200)}`,
    boxShadow: `0 20px 40px -12px ${darkVeil(0.18)}`,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
  },
}));

const steps = [
  'Informations personnelles',
  'Fonction et accès',
  'Identifiants',
  'Récapitulatif'
];

const CreateUserDialog = ({ open, onClose, onUserCreated }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Rôles activés sur cet espace : on choisit un rôle, qui impose le niveau
  // d'accès et le service. Sinon (tables non migrées, API indisponible) on
  // garde les deux listes historiques.
  const [roleOptions, setRoleOptions] = useState(null);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    functions: '',
    access_level: '',
    id_service: '',
    role_id: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    rolesApi.getRoles()
      .then((res) => {
        // Avant la migration : les rôles que la migration créera.
        if (!cancelled) setRoleOptions(toRoleOptions(res));
      })
      .catch(() => {
        if (!cancelled) setRoleOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedRole = roleOptions?.options.find((r) => r.value === formData.role_id) || null;

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setError('');
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        return formData.firstname.trim() && formData.lastname.trim();
      case 1:
        if (roleOptions) return formData.functions.trim() && formData.role_id !== '';
        return formData.functions.trim() && formData.access_level !== '' && formData.id_service !== '';
      case 2:
        return formData.email.trim() && formData.password.trim() && formData.password === formData.confirmPassword;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const accessToken = localStorage.getItem('accessToken');
      const userData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        functions: formData.functions,
        access_level: selectedRole ? selectedRole.access_level : parseInt(formData.access_level),
        id_service: selectedRole ? selectedRole.id_service : parseInt(formData.id_service),
        email: formData.email,
        password: formData.password
      };
      if (selectedRole && roleOptions.ready) {
        userData.role_id = selectedRole.value;
      }

      const response = await usersApi.createUser(userData, accessToken);
      
      if (response.status === 200) {
        onUserCreated(response.data);
        handleClose();
      } else {
        setError(response.message || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors de la création de l\'utilisateur');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setActiveStep(0);
              setFormData({
          firstname: '',
          lastname: '',
          functions: '',
          access_level: '',
          id_service: '',
          role_id: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      setError('');
      onClose();
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.firstname}
                onChange={handleChange('firstname')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.lastname}
                onChange={handleChange('lastname')}
                required
                margin="normal"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fonction"
                value={formData.functions}
                onChange={handleChange('functions')}
                required
                margin="normal"
              />
            </Grid>
            {roleOptions ? (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Rôle</InputLabel>
                  <Select
                    value={formData.role_id}
                    onChange={handleChange('role_id')}
                    label="Rôle"
                  >
                    {roleOptions.options.map((role) => (
                      <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
            <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Niveau d'accès</InputLabel>
                <Select
                  value={formData.access_level}
                  onChange={handleChange('access_level')}
                  label="Niveau d'accès"
                >
                  <MenuItem value={0}>Fort (0)</MenuItem>
                  <MenuItem value={1}>Faible (1)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.id_service}
                  onChange={handleChange('id_service')}
                  label="Service"
                >
                  <MenuItem value={0}>Administrateur (0)</MenuItem>
                  <MenuItem value={3}>Commercial (3)</MenuItem>
                  <MenuItem value={4}>Relance (4)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            </>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mot de passe"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                required
                margin="normal"
                error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
                helperText={formData.password !== formData.confirmPassword && formData.confirmPassword !== '' ? 'Les mots de passe ne correspondent pas' : ''}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Récapitulatif</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Prénom</Typography>
                <Typography variant="body1">{formData.firstname}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Nom</Typography>
                <Typography variant="body1">{formData.lastname}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Fonction</Typography>
                <Typography variant="body1">{formData.functions}</Typography>
              </Grid>
              {selectedRole ? (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Rôle</Typography>
                  <Typography variant="body1">{selectedRole.label}</Typography>
                </Grid>
              ) : (
              <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Niveau d'accès</Typography>
                <Typography variant="body1">
                  {formData.access_level === '0' ? 'Fort (0)' : 'Faible (1)'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Service</Typography>
                <Typography variant="body1">
                  {formData.id_service === '0' ? 'Administrateur (0)' : 
                   formData.id_service === '3' ? 'Commercial (3)' : 'Relance (4)'}
                </Typography>
              </Grid>
              </>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{formData.email}</Typography>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.25rem' }}>
        Créer un utilisateur
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.text.secondary, 0.07),
            color: 'text.secondary',
            border: (theme) => `1px solid ${alpha(theme.palette.text.secondary, 0.18)}`,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5
          }}
        >
          Annuler
        </Button>
        <Button
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.text.secondary, 0.07),
            color: 'text.secondary',
            border: (theme) => `1px solid ${alpha(theme.palette.text.secondary, 0.18)}`,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5
          }}
        >
          Retour
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!validateStep() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: grey(0),
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.25)}`,
            px: 3,
            '&:hover': { background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.dark} 100%)` },
            '&.Mui-disabled': { background: (theme) => `${theme.palette.divider}`, color: grey(0) }
          }}
        >
          {activeStep === steps.length - 1 ? 'Créer' : 'Suivant'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

CreateUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUserCreated: PropTypes.func.isRequired,
};

export default CreateUserDialog; 